import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { Pill } from "@/components/brand/Pill";
import { Stat } from "@/components/admin/reference-data/shared";
import { configTypeUi, packageReleaseLabel } from "@/lib/reference-data-config-types";
import type { ConfigPackage } from "@/types/reference-data";

/** Relatório do pacote: release, e por tabela a versão nova e os números da carga. */
export function PackageSanityReport({
  pkg,
  labelOf,
}: {
  pkg: ConfigPackage;
  labelOf: (table: string) => string;
}) {
  const { t } = useTranslation();
  const ok = pkg.state !== "failed";
  const { statFields } = configTypeUi(pkg.config_type);
  // Recusa e erro gravam as mesmas violações em todas as runs: mostra uma vez.
  const violations = [...new Set(pkg.runs.flatMap((r) => r.report?.violations ?? []))];
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {ok ? (
          <CheckCircle2 className="w-4 h-4 text-[#27a155]" />
        ) : (
          <XCircle className="w-4 h-4 text-[#d4353c]" />
        )}
        <span className="font-brand font-bold text-[15px] text-[#08254c]">{t("Sanity report")}</span>
        <span className="mono muted text-[11px]">{t("SAP release")}</span>
        <Pill tone="navy">{packageReleaseLabel(pkg)}</Pill>
      </div>
      {ok &&
        pkg.runs.map((run) => {
          const report = (run.report ?? {}) as Record<string, unknown>;
          const stats = statFields.filter(({ key }) => typeof report[key] === "number");
          return (
            <div key={run.table_name} className="border border-[#f2f4f7] rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[13px] font-semibold text-[#08254c]">{labelOf(run.table_name)}</span>
                <span className="flex items-center gap-1.5">
                  <span className="mono muted text-[10px]">{t("New version")}</span>
                  <span className="mono text-[12px] text-[#08254c]">{run.version ?? "—"}</span>
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map(({ key, label, accent }) => (
                  <Stat key={key} label={t(label)} value={report[key] as number} accent={accent} />
                ))}
              </div>
            </div>
          );
        })}
      {!ok && pkg.failure_reason && !violations.includes(pkg.failure_reason) && (
        <div className="text-[12px] text-[#b02a31] font-mono">{pkg.failure_reason}</div>
      )}
      {violations.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[#b02a31] mb-1.5">
            <AlertTriangle className="w-3 h-3" />
            {t("{{n}} violation(s)", { n: violations.length })}
          </div>
          <ul className="space-y-1">
            {violations.map((v) => (
              <li key={v} className="text-[12px] text-[#b02a31] font-mono">
                • {v}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
